import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import ChannelTracking from '@vp/channel-tracking';
import { useTracking } from '@vp/react-tracking';
import { useUserContext } from '@vp/ubik-context';
import { removePathQueryParams } from './removePathQueryParams';
import useIsEditMode from './useIsEditMode';

export type DefaultPageTrackingParams = {
    product_id?: string;
    core_product_version?: number;
    route?: string;
    host?: string;
};

export const DefaultPageTrackingParamsContext = createContext<DefaultPageTrackingParams | null>(
    null,
);

type Browser = {
    name?: string;
    version?: string;
    major?: string;
};

type OS = {
    name?: string;
    version?: string;
};

type TrackOnPageLoadProps = {
    label?: string;
    core_product_id?: string;
    variant?: string;
    browser?: Browser;
    os?: OS;
    deviceType?: string;
};

export function useTrackOnPageLoad(eventName: string, eventProps: TrackOnPageLoadProps) {
    const { tracking } = useTracking();
    const isEditMode = useIsEditMode();
    const ctx = useContext(DefaultPageTrackingParamsContext) ?? {};

    useEffect(() => {
        if (isEditMode) {
            return;
        }
        tracking.track(eventName, {
            ...ctx,
            ...eventProps,
            ...getUtmParams(),
        });
    }, [isEditMode]);
}

type UseTrackType = (eventName: string, eventProps: Record<string, string | string[]>) => undefined;

export function useTrack(): UseTrackType {
    const { tracking } = useTracking();
    const isEditMode = useIsEditMode();
    const ctx = useContext(DefaultPageTrackingParamsContext) ?? {};

    return (eventName, eventProps) => {
        if (isEditMode) {
            return;
        }
        tracking.track(eventName, {
            ...ctx,
            ...eventProps,
            ...getUtmParams(),
        });
    };
}

export function useTrackLink(eventName: string, eventProps: Record<string, string | string[]>) {
    const { tracking } = useTracking();
    const isEditMode = useIsEditMode();
    const ctx = useContext(DefaultPageTrackingParamsContext) ?? {};

    return useCallback(
        (node: HTMLElement) => {
            if (isEditMode) {
                return;
            }
            if (node !== null) {
                tracking.trackLink(node, eventName, {
                    ...ctx,
                    ...eventProps,
                    ...getUtmParams(),
                });
            }
        },

        [isEditMode],
    );
}

export function useTrackOnComponentView(
    eventName: string,
    eventProps: Record<string, string | string[]>,
) {
    const { tracking } = useTracking();
    const isEditMode = useIsEditMode();
    const ctx = useContext(DefaultPageTrackingParamsContext) ?? {};
    const ref = useRef(null);

    useEffect(() => {
        if (isEditMode) {
            return;
        }

        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    tracking.track(eventName, {
                        ...ctx,
                        ...eventProps,
                        ...getUtmParams(),
                    });
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(element);
        return () => {
            if (element) {
                observer.disconnect();
            }
        };
    }, [isEditMode]);

    return ref;
}

function getUtmParams() {
    return {
        utmSource: ChannelTracking.getTrackingData().utm_source || '',
        utmId: ChannelTracking.getTrackingData().utm_id || '',
    };
}

export function useTrackNavigationClicked(
    category: string,
    pageZone: string,
    url: string,
    ctaValue: string,
): (node: HTMLElement) => void {
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);
    const destinationPagePath = removePathQueryParams(url);

    return useTrackLink('Navigation Clicked', {
        category,
        label: `Content:${pageZone}`,
        eventDetail: `${currentPagePath};${destinationPagePath};${pageZone};${ctaValue}`,
        navigationDetail: ctaValue,
    });
}

export function useTrackPromotionViewed(
    category: string,
    pageZone: string,
    url: string,
    ctaValue: string,
    creative: string,
    position: number,
) {
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);
    const destinationPagePath = removePathQueryParams(url);

    return useTrackOnComponentView('Promotion Viewed', {
        category,
        label: `Content:${pageZone}`,
        eventDetail: `${currentPagePath};${destinationPagePath};${pageZone};${ctaValue}`,
        navigationDetail: ctaValue,
        promotion_id: ctaValue,
        creative,
        creative_slot: `${pageZone} - ${position}`,
        name: `${pageZone} - ${ctaValue}`,
    });
}
